"use client";

import { useEffect, type ReactNode } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";

import { analyticsConfig } from "@/lib/analytics/config";

/**
 * Initialise PostHog at module evaluation time — not inside useEffect.
 *
 * Why: React's useEffect ordering runs children's effects before parents'.
 * If init() lived inside the provider's useEffect, the child
 * `<PageviewTracker />` would call `posthog.capture("$pageview")` BEFORE
 * init finished, and the very first pageview never registered against a
 * valid session. Module-level init fires as soon as this file is imported
 * by any client component (layout root), guaranteeing init is complete
 * before any capture call.
 *
 * The `typeof window` check keeps this safe on the server.
 */
let initialised = false;

function initPostHogIfNeeded(): void {
  if (initialised) return;
  if (typeof window === "undefined") return;
  const key = analyticsConfig.posthog.key;
  if (!key) return;

  posthog.init(key, {
    api_host: analyticsConfig.posthog.apiHost,
    ui_host: analyticsConfig.posthog.uiHost,
    person_profiles: "identified_only",
    capture_pageview: false, // handled manually for App Router route changes
    capture_pageleave: true,
    autocapture: true,
    capture_performance: {
      web_vitals: true,
      network_timing: false,
    },
  });
  initialised = true;
}

// Fire init eagerly on module load (client only).
initPostHogIfNeeded();

export function PostHogProvider({ children }: { children: ReactNode }) {
  // Idempotent safety net in case the module was tree-shaken before the
  // eager call had a chance to run.
  useEffect(() => {
    initPostHogIfNeeded();
  }, []);

  return (
    <>
      <PageviewTracker />
      {children}
    </>
  );
}

function PageviewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!analyticsConfig.posthog.key) return;
    if (!pathname) return;

    const qs = searchParams?.toString();
    const url = qs ? `${pathname}?${qs}` : pathname;

    posthog.capture("$pageview", { $current_url: url });
  }, [pathname, searchParams]);

  return null;
}
